import React, { useEffect, useRef, useState } from 'react';
import { FaCreditCard, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import {
  checkoutSubscriptionCinetPay,
  checkoutSubscriptionPiSpi,
  getSubscriptionPiSpiStatus,
} from '../services/api';
import waveLogo from '../assets/wave-logo.jpg';
import cinetpayLogo from '../assets/cinetpay-logo.jpg';
import pispiLogo from '../assets/pispi-logo.png';

const PAYMENT_METHODS = {
  cinetpay: { label: 'CinetPay', logo: cinetpayLogo },
  pispi: { label: 'PI-SPI', logo: pispiLogo },
  wave_ci: { label: 'Wave', logo: waveLogo },
};

const PISPI_POLL_INTERVAL_MS = 3000;

const SubscriptionPaymentModal = ({ isOpen, onClose, plan, planKey, billingCycle, onSuccess }) => {
  const { user } = useAuth();
  const [checkingOut, setCheckingOut] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentData, setPaymentData] = useState({
    method: 'cinetpay',
    billingEmail: user?.email || '',
    mobileNumber: '',
    payeurAlias: '',
  });
  const [pispiRequest, setPispiRequest] = useState(null);
  const pispiPollRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pispiPollRef.current) clearInterval(pispiPollRef.current);
    };
  }, []);

  if (!isOpen || !plan) return null;

  const price = billingCycle === 'yearly' ? Number(plan.price_yearly || 0) : Number(plan.price_monthly ?? plan.price ?? 0);
  const activePaymentLabel = (PAYMENT_METHODS[paymentData.method] || PAYMENT_METHODS.cinetpay).label;

  const handlePaymentChange = (field, value) => {
    setPaymentData(prev => ({ ...prev, [field]: value }));
    setPaymentError('');
  };

  const validatePaymentForm = () => {
    if (
      paymentData.method === 'cinetpay'
      && (!paymentData.billingEmail.trim() || !paymentData.billingEmail.includes('@'))
    ) {
      return 'Saisissez un e-mail de facturation valide.';
    }

    if (paymentData.method === 'wave_ci') {
      const digits = paymentData.mobileNumber.replace(/\D/g, '');
      if (digits.length < 8 || digits.length > 14) return 'Saisissez un numéro Wave valide.';
    }

    if (paymentData.method === 'pispi' && paymentData.payeurAlias.trim().length < 3) {
      return 'Saisissez votre alias PI-SPI.';
    }

    return '';
  };

  const stopPispiPolling = () => {
    if (pispiPollRef.current) {
      clearInterval(pispiPollRef.current);
      pispiPollRef.current = null;
    }
  };

  const startPispiPolling = (txId) => {
    pispiPollRef.current = setInterval(async () => {
      try {
        const response = await getSubscriptionPiSpiStatus(txId);
        const paymentStatus = response?.payment_status;

        if (paymentStatus === 'completed') {
          stopPispiPolling();
          onSuccess?.(planKey, response);
          setPispiRequest(null);
          onClose();
        } else if (paymentStatus === 'failed') {
          stopPispiPolling();
          setPispiRequest(prev => (prev ? { ...prev, status: 'failed' } : prev));
        }
      } catch (err) {
        // Transient network/API errors during polling are ignored; polling continues until expiry.
      }
    }, PISPI_POLL_INTERVAL_MS);
  };

  const handleCheckout = async (e) => {
    e.preventDefault();

    const validationError = validatePaymentForm();
    if (validationError) {
      setPaymentError(validationError);
      return;
    }

    setCheckingOut(true);
    setPaymentError('');
    try {
      if (paymentData.method === 'pispi') {
        const response = await checkoutSubscriptionPiSpi(planKey, billingCycle, paymentData.payeurAlias.trim());

        if (!response?.tx_id) {
          throw new Error("La demande de paiement PI-SPI n'a pas été créée.");
        }

        setPispiRequest({ txId: response.tx_id, status: 'pending' });
        startPispiPolling(response.tx_id);
        return;
      }

      const response = await checkoutSubscriptionCinetPay(planKey, billingCycle, {
        method: paymentData.method,
        mobile_number: paymentData.mobileNumber,
        billing_email: paymentData.billingEmail || user?.email || '',
      });

      if (!response?.payment_url) {
        throw new Error("CinetPay n'a pas renvoyé de lien de paiement.");
      }

      window.location.assign(response.payment_url);
    } catch (err) {
      setPaymentError(err.message || 'Le paiement a échoué');
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={() => !checkingOut && onClose()}
    >
      <form
        onSubmit={handleCheckout}
        style={{
          width: '100%',
          maxWidth: '520px',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: '#FFFFFF',
          borderRadius: '14px',
          padding: '1.5rem',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--primary-orange)' }}>Paiement de l'abonnement</h3>
            <p style={{ margin: '0.25rem 0 0', color: 'var(--gray)', fontSize: '0.9rem' }}>
              {plan.name} · {Number(price).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} F / {billingCycle === 'yearly' ? 'an' : 'mois'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={checkingOut}
            style={{
              border: 'none',
              background: 'var(--bg)',
              color: 'var(--dark-gray)',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              cursor: checkingOut ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FaTimes />
          </button>
        </div>

        {paymentError && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>{paymentError}</div>
        )}

        {paymentData.method === 'cinetpay' && (
          <div className="form-group">
            <label>E-mail de facturation</label>
            <input
              type="email"
              value={paymentData.billingEmail}
              onChange={e => handlePaymentChange('billingEmail', e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
          </div>
        )}

        {paymentData.method === 'wave_ci' && (
          <div className="form-group">
            <label>Numéro {activePaymentLabel}</label>
            <input
              type="tel"
              inputMode="tel"
              value={paymentData.mobileNumber}
              onChange={e => handlePaymentChange('mobileNumber', e.target.value.replace(/[^\d+ ]/g, '').slice(0, 18))}
              placeholder="+225 07 00 00 00 00"
              autoComplete="tel"
            />
          </div>
        )}

        {paymentData.method === 'pispi' && !pispiRequest && (
          <div className="form-group">
            <label>Votre alias PI-SPI</label>
            <input
              type="text"
              value={paymentData.payeurAlias}
              onChange={e => handlePaymentChange('payeurAlias', e.target.value)}
              placeholder="Alias enregistré sur votre application PI-SPI"
              autoComplete="off"
            />
            <p style={{ margin: '0.5rem 0 0', color: 'var(--gray)', fontSize: '0.85rem' }}>
              Une demande de paiement de {Number(price).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} F sera envoyée à votre application PI-SPI. Vous aurez environ 2 minutes pour la confirmer.
            </p>
          </div>
        )}

        {pispiRequest && (
          <div style={{
            border: `1px solid ${pispiRequest.status === 'failed' ? 'rgba(239, 68, 68, 0.35)' : 'var(--border)'}`,
            borderRadius: '10px',
            padding: '1rem',
            margin: '1rem 0',
            background: pispiRequest.status === 'failed' ? 'rgba(239, 68, 68, 0.12)' : 'var(--surface-2)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
          }}>
            {pispiRequest.status === 'failed' ? (
              <FaExclamationTriangle style={{ color: '#F87171', width: '22px', height: '22px', flexShrink: 0, marginTop: '2px' }} />
            ) : (
              <div className="spinner" style={{ width: '22px', height: '22px', flexShrink: 0 }} />
            )}
            <div>
              <strong style={{ color: 'var(--dark-gray)', display: 'block', marginBottom: '0.35rem' }}>
                {pispiRequest.status === 'failed' ? 'Paiement PI-SPI refusé ou expiré' : 'En attente de confirmation PI-SPI'}
              </strong>
              <p style={{ margin: 0, color: 'var(--gray)', fontSize: '0.85rem' }}>
                {pispiRequest.status === 'failed'
                  ? 'La demande a été rejetée, annulée ou a expiré. Vous pouvez réessayer.'
                  : `Ouvrez votre application PI-SPI pour confirmer le paiement de ${Number(price).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} F. Transaction ${pispiRequest.txId}.`}
              </p>
              {pispiRequest.status === 'failed' && (
                <button
                  type="button"
                  onClick={() => { stopPispiPolling(); setPispiRequest(null); }}
                  style={{
                    marginTop: '0.6rem',
                    border: 'none',
                    background: 'var(--primary-orange)',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '0.5rem 0.9rem',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '0.82rem',
                  }}
                >
                  Réessayer
                </button>
              )}
            </div>
          </div>
        )}

        {!pispiRequest && (
          <button
            type="submit"
            disabled={checkingOut}
            className="btn-submit"
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '0.45rem',
              marginTop: '1rem',
            }}
          >
            <FaCreditCard />{' '}
            {checkingOut
              ? (paymentData.method === 'pispi' ? 'Envoi de la demande PI-SPI...' : 'Ouverture de CinetPay...')
              : 'Payer'}
          </button>
        )}
      </form>
    </div>
  );
};

export default SubscriptionPaymentModal;
